/**
 * Campaign Server Functions
 *
 * This module provides TanStack Start server functions for campaign-related
 * API calls. These functions wrap Orval-generated API functions to provide
 * server-side execution with automatic cookie forwarding via the custom instance.
 */

import { createServerFn } from "@tanstack/react-start";
import type {
  CampaignAttachmentResponse,
  CampaignCreateRequest,
  CampaignOutgoingTaskItemResponse as GeneratedCampaignOutgoingTaskItemResponse,
  CampaignOutgoingTaskListResponse as GeneratedCampaignOutgoingTaskListResponse,
  CampaignOutgoingTaskUpdateRequest,
  CampaignQuoteSupplierQuestionReplyRequest,
  CampaignQuoteStatus,
  CampaignQuoteStatusUpdateRequest,
  CampaignSettingResponse,
  CampaignICPListResponse,
  CampaignResponse as GeneratedCampaignResponse,
  CampaignKnowledgeLinkListResponse,
  CampaignKnowledgeLinkResponse,
  CustomerCaseCreateRequest,
  CustomerCaseListResponse,
  CustomerCaseResponse,
  CustomerCaseUpdateRequest,
  DraftType,
  DraftValidationStatus,
  ListCampaignMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdMessagesGetParams,
  LeadsListResponse,
  ListLeadsDetailForCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdCompaniesLeadsGetParams,
  MessageDraftStatus,
  MessageListResponse,
  OutreachChannel,
  SequenceStepRequest,
  SendTaskStatus,
  SenderAccountWithChannel,
  TargetCompanyStatus,
  UpdateCompanyStatusRequest,
} from "@/lib/api/generated";
import { AttachmentType } from "@/lib/api/generated";
import {
  createCampaignApiWorkspacesWorkspaceIdCampaignsPost,
  listCampaignsApiWorkspacesWorkspaceIdCampaignsGet,
  getCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdGet,
  getCampaignBusinessApiWorkspacesWorkspaceIdCampaignsCampaignIdBusinessGet,
  getCampaignAttachmentsApiWorkspacesWorkspaceIdCampaignsCampaignIdAttachmentsGet,
  getOssUploadSignatureApiWorkspacesWorkspaceIdCampaignsCampaignIdOssUploadSignaturePost,
  createAttachmentRecordApiWorkspacesWorkspaceIdCampaignsCampaignIdAttachmentsPost,
  updateCampaignBusinessLogoApiWorkspacesWorkspaceIdCampaignsCampaignIdBusinessLogoPatch,
  updateCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdPatch,
  getCampaignSettingApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingGet,
  listCampaignIcpsApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpsGet,
  saveCampaignSettingApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingPost,
  updateCampaignSettingSequenceApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingSequencePatch,
  listCampaignMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdMessagesGet,
  validateIcpCoverageApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpValidateCoveragePost,
  listCampaignProductsApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsGet,
  addProductToCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsProductIdPost,
  removeProductFromCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsProductIdDelete,
  listCampaignKnowledgesApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesGet,
  addKnowledgeToCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesKnowledgeSourceIdPost,
  removeKnowledgeFromCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesKnowledgeSourceIdDelete,
  listCampaignQuotesApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesGet,
  getCampaignRfqFormApiWorkspacesWorkspaceIdCampaignsCampaignIdRfqGet,
  replyCampaignQuoteSupplierQuestionApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesQuoteIdSupplierQuestionReplyPost,
  updateCampaignQuoteStatusApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesQuoteIdPatch,
} from "@/lib/api/generated/campaigns/campaigns";
import { updateCompanyStatusApiWorkspacesWorkspaceIdCampaignsCampaignIdCompaniesCompanyIdStatusPatch } from "@/lib/api/generated/campaign-target-companies/campaign-target-companies";
import {
  createCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesPost,
  deleteCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesCaseIdDelete,
  listCustomerCasesApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesGet,
  updateCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesCaseIdPut,
} from "@/lib/api/generated/customer-cases/customer-cases";
import { customInstance } from "@/lib/api/custom-instance";
import { extractApiErrorMessage, extractApiErrorStatus } from "@/lib/api/error";
import { normalizeFoundedYear } from "@/components/campaign/forms/business-form/schema";
import { getMeaningfulCriteria } from "@/components/campaign/forms/icp-form/schema";
import {
  normalizeCampaignQuoteListResponse,
  normalizeCampaignQuoteResponse,
  type SerializableInquiryProduct,
} from "@/lib/campaign/serializable-quote";
import { logInfo, logError } from "@/lib/structured-log";
import {
  normalizeCampaignListResponse,
  normalizeCampaignResponse,
  type CampaignResponse,
} from "@/types/campaign";
import type { BusinessFormValues } from "@/components/campaign/forms/business-form";
import type { ICPFormValues } from "@/components/campaign/forms/icp-form/schema";
import type { SettingFormValues } from "@/components/campaign/forms/setting-form/schema";

function isGeneratedCampaignResponse(value: unknown): value is GeneratedCampaignResponse {
  return Boolean(value && typeof value === "object" && "agent_mode" in value);
}

function isSubscriptionRequiredError(detail: unknown): boolean {
  if (typeof detail === "string") {
    return detail.trim().toLowerCase() === "subscription not found";
  }

  if (detail && typeof detail === "object" && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") {
      return message.trim().toLowerCase() === "subscription not found";
    }
  }
  return false;
}

function toPlainJson(value: unknown, seen = new WeakSet<object>()): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => toPlainJson(item, seen));
  }

  if (typeof value === "object") {
    const obj = value as object;
    if (seen.has(obj)) {
      return "[Circular]";
    }

    seen.add(obj);
    const result: Record<string, unknown> = {};

    for (const [key, v] of Object.entries(obj as Record<string, unknown>)) {
      const converted = toPlainJson(v, seen);
      if (converted !== undefined) {
        result[key] = converted;
      }
    }

    return result;
  }

  return String(value);
}

const DEFAULT_VISIBLE_TARGET_STATUSES = ["pending", "contacted", "qualified"] as const;

type SerializablePrimitive = string | number | boolean | null;

interface SerializableObject {
  [key: string]: SerializableValue;
}

interface SerializableArray {
  [index: number]: SerializableValue;
  length: number;
}

type SerializableValue = SerializablePrimitive | SerializableObject | SerializableArray;

export interface CampaignOutgoingTaskItem {
  draft_id: string;
  send_task_id: string | null;
  deal_id: string | null;
  thread_id: string | null;
  recipient_name: string | null;
  recipient_company: string | null;
  recipient_address: string | null;
  channel: OutreachChannel;
  draft_type: DraftType;
  subject: string | null;
  body: string;
  body_preview: string;
  scheduled_at: string | null;
  draft_status: MessageDraftStatus;
  send_task_status: SendTaskStatus | null;
  edited_by_user: boolean;
  validation_status: DraftValidationStatus;
  blocked_reason: string | null;
  generation_context: Record<string, SerializableValue>;
  version: number;
  can_edit: boolean;
  created_at: string;
  updated_at: string;
}

export interface CampaignOutgoingTaskListResponse {
  items: CampaignOutgoingTaskItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

function normalizeGenerationContext(
  value: GeneratedCampaignOutgoingTaskItemResponse["generation_context"],
): Record<string, SerializableValue> {
  if (!value || typeof value !== "object") {
    return {};
  }

  return value as Record<string, SerializableValue>;
}

function normalizeCampaignOutgoingTaskItem(
  task: GeneratedCampaignOutgoingTaskItemResponse,
): CampaignOutgoingTaskItem {
  return {
    draft_id: task.draft_id,
    send_task_id: task.send_task_id ?? null,
    deal_id: task.deal_id ?? null,
    thread_id: task.thread_id ?? null,
    recipient_name: task.recipient_name ?? null,
    recipient_company: task.recipient_company ?? null,
    recipient_address: task.recipient_address ?? null,
    channel: task.channel,
    draft_type: task.draft_type,
    subject: task.subject ?? null,
    body: task.body,
    body_preview: task.body_preview,
    scheduled_at: task.scheduled_at ?? null,
    draft_status: task.draft_status,
    send_task_status: task.send_task_status ?? null,
    edited_by_user: task.edited_by_user,
    validation_status: task.validation_status,
    blocked_reason: task.blocked_reason ?? null,
    generation_context: normalizeGenerationContext(task.generation_context),
    version: task.version,
    can_edit: task.can_edit,
    created_at: task.created_at,
    updated_at: task.updated_at,
  };
}

function normalizeCampaignOutgoingTaskListResponse(
  response: GeneratedCampaignOutgoingTaskListResponse,
): CampaignOutgoingTaskListResponse {
  const rawResponse = response as GeneratedCampaignOutgoingTaskListResponse & {
    page?: number;
    page_size?: number;
    total_pages?: number;
  };
  return {
    items: (response.items ?? []).map(normalizeCampaignOutgoingTaskItem),
    total: response.total ?? 0,
    page: rawResponse.page ?? 1,
    page_size: rawResponse.page_size ?? Math.max((response.items ?? []).length, 1),
    total_pages: rawResponse.total_pages ?? 1,
  };
}

/**
 * Create campaign
 *
 * Server function that creates a new campaign in a workspace.
 * Requires authentication and workspace access.
 */
export const createCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaign: CampaignCreateRequest }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.create.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_name: data.campaign.name,
      campaign_agent_mode: data.campaign.agent_mode ?? "sales",
    });

    try {
      const response = await createCampaignApiWorkspacesWorkspaceIdCampaignsPost(
        data.workspaceId,
        data.campaign,
      );

      if (response.status !== 201) {
        const status = Number(response.status);
        const fallbackMessage = "Failed to create campaign";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        let code: string | undefined;
        let details: unknown;
        let detailPayload: unknown;

        const rawData = (response as { data?: unknown }).data;
        if (rawData && typeof rawData === "object" && "detail" in rawData) {
          const detail = (rawData as { detail?: unknown }).detail;
          detailPayload = detail;
          if (detail && typeof detail === "object") {
            const detailObj = detail as { code?: unknown; details?: unknown };
            if (typeof detailObj.code === "string") {
              code = detailObj.code;
            }
            if (detailObj.details && typeof detailObj.details === "object") {
              details = detailObj.details;
            }
          }
        }

        if (!code) {
          if (status === 404 && isSubscriptionRequiredError(detailPayload)) {
            code = "billing.subscription_required";
          } else if (status === 403) {
            code = "campaign.access_denied";
          } else if (status === 400 || status === 409 || status === 422) {
            code = "campaign.validation_failed";
          } else if (status === 404) {
            code = "campaign.not_found";
          } else {
            code = "internal.error";
          }
        }

        logError("campaigns.create.failed", {
          status,
          workspace_id: data.workspaceId,
          code,
          error: errorMessage,
          details,
        });

        throw new Error(
          JSON.stringify({
            code,
            detail: errorMessage,
            ...(details ? { details } : {}),
            status,
          }),
        );
      }

      logInfo("campaigns.create.success", {
        campaign_id: response.data.id,
        workspace_id: data.workspaceId,
      });

      return normalizeCampaignResponse(response.data);
    } catch (error) {
      if (error instanceof Error) {
        const parsed = (() => {
          try {
            return JSON.parse(error.message) as {
              code?: string;
            };
          } catch {
            return undefined;
          }
        })();
        if (parsed && typeof parsed.code === "string") {
          logError("campaigns.create.error", {
            error: error.message,
            workspace_id: data.workspaceId,
            code: parsed.code,
          });
          throw error;
        }
      }

      const fallbackMessage = "Failed to create campaign";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      let code: string;
      if (status === 404 && isSubscriptionRequiredError(errorMessage)) {
        code = "billing.subscription_required";
      } else if (status === 403) {
        code = "campaign.access_denied";
      } else if (status === 400 || status === 409 || status === 422) {
        code = "campaign.validation_failed";
      } else if (status === 404) {
        code = "campaign.not_found";
      } else {
        code = "internal.error";
      }

      logError("campaigns.create.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        code,
        status,
      });

      throw new Error(
        JSON.stringify({
          code,
          detail: errorMessage,
          status,
        }),
      );
    }
  });

/**
 * List campaigns
 *
 * Server function that retrieves all campaigns in a workspace.
 * Requires authentication and workspace access.
 */
export const listCampaignsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.list.server_fn_called", {
      workspace_id: data.workspaceId,
    });

    try {
      const response = await listCampaignsApiWorkspacesWorkspaceIdCampaignsGet(data.workspaceId);

      if (response.status !== 200) {
        logError("campaigns.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaigns",
            status: response.status,
          }),
        );
      }

      return normalizeCampaignListResponse(response.data);
    } catch (error) {
      logError("campaigns.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * Get campaign
 *
 * Server function that retrieves a specific campaign.
 * Requires authentication and workspace access.
 */
export const getCampaignServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.get.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response = await getCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdGet(
        data.workspaceId,
        data.campaignId,
      );

      if (response.status !== 200) {
        logError("campaigns.get.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get campaign",
            status: response.status,
          }),
        );
      }

      return normalizeCampaignResponse(response.data);
    } catch (error) {
      logError("campaigns.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

type CampaignStatusAction = "activate" | "pause" | "resume";

async function mutateCampaignStatus({
  workspaceId,
  campaignId,
  action,
  logKey,
}: {
  workspaceId: string;
  campaignId: string;
  action: CampaignStatusAction;
  logKey: string;
}): Promise<CampaignResponse> {
  logInfo(`campaigns.${logKey}.server_fn_called`, {
    workspace_id: workspaceId,
    campaign_id: campaignId,
  });

  try {
    const response = await customInstance<{
      data: GeneratedCampaignResponse;
      status: number;
      headers: Headers;
    }>(`/api/workspaces/${workspaceId}/campaigns/${campaignId}/${action}`, {
      method: "POST",
    });

    if (response.status !== 200) {
      logError(`campaigns.${logKey}.failed`, {
        status: response.status,
        workspace_id: workspaceId,
        campaign_id: campaignId,
      });
      throw new Error(
        JSON.stringify({
          detail: `Failed to ${action} campaign`,
          status: response.status,
        }),
      );
    }

    return normalizeCampaignResponse(response.data);
  } catch (error) {
    logError(`campaigns.${logKey}.error`, {
      error: error instanceof Error ? error.message : "unknown-error",
      workspace_id: workspaceId,
      campaign_id: campaignId,
    });
    throw error;
  }
}

export const activateCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    const campaignResponse = await getCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdGet(
      data.workspaceId,
      data.campaignId,
    );
    const campaignStatus = campaignResponse.status as number;

    const campaignData =
      campaignStatus === 200 && isGeneratedCampaignResponse(campaignResponse.data)
        ? campaignResponse.data
        : null;
    let hasLaunchReadyIcp = false;

    if (campaignData?.agent_mode === "sourcing" || campaignData?.agent_mode === "sales") {
      const icpResponse = await listCampaignIcpsApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpsGet(
        data.workspaceId,
        data.campaignId,
        { status: "confirmed" },
      );
      const icpList =
        icpResponse.status === 200 ? (icpResponse.data as CampaignICPListResponse) : null;
      hasLaunchReadyIcp = Boolean(icpList?.icps?.some((icp) => icp.status === "confirmed"));
    }

    const requiresIcp =
      campaignData?.agent_mode === "sourcing" || campaignData?.agent_mode === "sales";
    const missingIcp = requiresIcp && !hasLaunchReadyIcp;

    if (missingIcp) {
      logError("campaigns.launch.validation.failed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        agent_mode: campaignData?.agent_mode ?? "unknown",
        missing_icp: missingIcp,
      });

      throw new Error(
        JSON.stringify({
          detail: "Missing campaign ICP data for launch",
          status: 400,
          code: "MISSING_CAMPAIGN_PROFILE",
          missing_icp: missingIcp,
        }),
      );
    }

    logInfo("campaigns.launch.validation.pass", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      agent_mode: campaignData?.agent_mode ?? "unknown",
    });

    return mutateCampaignStatus({
      workspaceId: data.workspaceId,
      campaignId: data.campaignId,
      action: "activate",
      logKey: "activate",
    });
  });

export const pauseCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) =>
    mutateCampaignStatus({
      workspaceId: data.workspaceId,
      campaignId: data.campaignId,
      action: "pause",
      logKey: "pause",
    }),
  );

export const resumeCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) =>
    mutateCampaignStatus({
      workspaceId: data.workspaceId,
      campaignId: data.campaignId,
      action: "resume",
      logKey: "resume",
    }),
  );

/**
 * List campaign messages
 *
 * Server function that retrieves paginated messages for a specific campaign.
 * Supports optional pagination via `page` and `page_size`.
 */
export const listCampaignMessagesServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      params?: ListCampaignMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdMessagesGetParams;
    }) => data,
  )
  .handler(async ({ data }): Promise<MessageListResponse> => {
    logInfo("campaigns.messages.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      page: data.params?.page,
      page_size: data.params?.page_size,
    });

    try {
      const response =
        await listCampaignMessagesApiWorkspacesWorkspaceIdCampaignsCampaignIdMessagesGet(
          data.workspaceId,
          data.campaignId,
          data.params,
        );

      const status = response.status as number;
      if (status !== 200) {
        logError("campaigns.messages.list.failed", {
          status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaign messages",
            status,
          }),
        );
      }

      return response.data as MessageListResponse;
    } catch (error) {
      logError("campaigns.messages.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const listCampaignOutgoingTasksServer = createServerFn({ method: "GET" })
  .validator(
    (data: { workspaceId: string; campaignId: string; page?: number; pageSize?: number }) => data,
  )
  .handler(async ({ data }): Promise<CampaignOutgoingTaskListResponse> => {
    logInfo("campaigns.tasks.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      page: data.page ?? 1,
      page_size: data.pageSize ?? 20,
    });

    try {
      const searchParams = new URLSearchParams();
      searchParams.set("page", String(data.page ?? 1));
      searchParams.set("page_size", String(data.pageSize ?? 20));
      const queryString = searchParams.toString();
      const url = queryString
        ? `/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/tasks?${queryString}`
        : `/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/tasks`;

      const response = await customInstance<{
        data: GeneratedCampaignOutgoingTaskListResponse;
        status: number;
        headers: Headers;
      }>(url, {
        method: "GET",
      });

      if (response.status !== 200) {
        logError("campaigns.tasks.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaign outgoing tasks",
            status: response.status,
          }),
        );
      }

      return normalizeCampaignOutgoingTaskListResponse(response.data);
    } catch (error) {
      logError("campaigns.tasks.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const updateCampaignOutgoingTaskDraftServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      draftId: string;
      request: CampaignOutgoingTaskUpdateRequest;
    }) => data,
  )
  .handler(async ({ data }): Promise<CampaignOutgoingTaskItem> => {
    logInfo("campaigns.tasks.update.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      draft_id: data.draftId,
      version: data.request.version,
    });

    try {
      const response = await customInstance<{
        data: GeneratedCampaignOutgoingTaskItemResponse;
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/tasks/${data.draftId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.request),
      });

      if (response.status !== 200) {
        logError("campaigns.tasks.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          draft_id: data.draftId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to update campaign outgoing task",
            status: response.status,
          }),
        );
      }

      return normalizeCampaignOutgoingTaskItem(response.data);
    } catch (error) {
      logError("campaigns.tasks.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        draft_id: data.draftId,
      });
      throw error;
    }
  });

export const deleteCampaignOutgoingTaskServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; draftId: string }) => data)
  .handler(async ({ data }): Promise<CampaignOutgoingTaskItem> => {
    logInfo("campaigns.tasks.delete.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      draft_id: data.draftId,
    });

    try {
      const response = await customInstance<{
        data: GeneratedCampaignOutgoingTaskItemResponse;
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/tasks/${data.draftId}`, {
        method: "DELETE",
      });

      if (response.status !== 200) {
        logError("campaigns.tasks.delete.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          draft_id: data.draftId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to delete campaign outgoing task",
            status: response.status,
          }),
        );
      }

      return normalizeCampaignOutgoingTaskItem(response.data);
    } catch (error) {
      logError("campaigns.tasks.delete.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        draft_id: data.draftId,
      });
      throw error;
    }
  });

/**
 * List campaign leads with details
 *
 * Server function that retrieves paginated leads (companies with contacts) for a specific campaign.
 * Supports optional pagination via `page` and `page_size`, as well as filtering by `stage` and sorting via `orderBy`.
 */
export const listCampaignLeadsDetailServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      params?: ListLeadsDetailForCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdCompaniesLeadsGetParams;
    }) => data,
  )
  // @ts-expect-error - Orval generates result_detail with unknown type but TanStack expects {}
  // This is a type system incompatibility, runtime behavior is correct
  .handler(async ({ data }): Promise<LeadsListResponse> => {
    const params = {
      ...data.params,
      status: data.params?.status ?? [...DEFAULT_VISIBLE_TARGET_STATUSES],
    };
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) {
      searchParams.append("page", String(params.page));
    }
    if (params.page_size !== undefined) {
      searchParams.append("page_size", String(params.page_size));
    }
    if (params.orderBy) {
      searchParams.append("orderBy", params.orderBy);
    }
    if (Array.isArray(params.status)) {
      params.status.forEach((status) => {
        if (status) {
          searchParams.append("status", status);
        }
      });
    }
    const queryString = searchParams.toString();
    const url = queryString
      ? `/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/companies/leads?${queryString}`
      : `/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/companies/leads`;

    logInfo("campaigns.leads.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      page: params.page,
      page_size: params.page_size,
      statuses: params.status ?? undefined,
      orderBy: params.orderBy ?? undefined,
    });

    try {
      const response = await customInstance<{
        data: LeadsListResponse;
        status: number;
        headers: Headers;
      }>(url, {
        method: "GET",
      });

      const status = response.status as number;
      if (status !== 200) {
        logError("campaigns.leads.list.failed", {
          status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaign leads",
            status,
          }),
        );
      }

      return response.data as LeadsListResponse;
    } catch (error) {
      logError("campaigns.leads.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const updateCampaignLeadStatusServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      companyId: string;
      status: TargetCompanyStatus;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.leads.update_status.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      company_id: data.companyId,
      status: data.status,
    });

    try {
      const payload: UpdateCompanyStatusRequest = {
        status: data.status,
      };
      const response =
        await updateCompanyStatusApiWorkspacesWorkspaceIdCampaignsCampaignIdCompaniesCompanyIdStatusPatch(
          data.workspaceId,
          data.campaignId,
          data.companyId,
          payload,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to update campaign lead status";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.leads.update_status.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          company_id: data.companyId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.leads.update_status.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        company_id: data.companyId,
        status: response.data.status,
      });

      return response.data;
    } catch (error) {
      const fallbackMessage = "Failed to update campaign lead status";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.leads.update_status.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        company_id: data.companyId,
        status,
      });
      throw error;
    }
  });

// ========================================
// Campaign Business & Attachments Server Functions
// ========================================

/**
 * Get campaign business
 *
 * Server function that retrieves the persisted business data of a campaign.
 * Returns null if no business data has been committed yet.
 */
export const getCampaignBusinessServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.get_business.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await getCampaignBusinessApiWorkspacesWorkspaceIdCampaignsCampaignIdBusinessGet(
          data.workspaceId,
          data.campaignId,
        );

      if (response.status !== 200) {
        logError("campaigns.get_business.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get campaign business",
            status: response.status,
          }),
        );
      }

      // Transform API response to BusinessFormValues compatible format
      const apiData = response.data;
      if (!apiData) {
        logInfo("campaigns.get_business.empty", {
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        return null;
      }

      const businessTypeArray =
        apiData.business_type && typeof apiData.business_type === "string"
          ? apiData.business_type
              .split(",")
              .map((item: string) => item.trim())
              .filter((item: string) => item.length > 0)
          : [];
      const formData = {
        name: apiData.name,
        location: apiData.location,
        founded_in: normalizeFoundedYear(apiData.founded_in),
        business_type: businessTypeArray,
        description: apiData.description,
        website: apiData.website,
        logo: apiData.logo ?? undefined,
        selling_points: apiData.selling_points,
        products: apiData.products,
      };

      return {
        formData,
        logoUrl: apiData.logo_url ?? null,
      };
    } catch (error) {
      // If it's a 404 error, return null instead of throwing
      if (error instanceof Error && error.message.includes("404")) {
        logInfo("campaigns.get_business.not_found", {
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        return null;
      }

      logError("campaigns.get_business.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Get campaign attachments
 *
 * Server function that retrieves all attachments for a campaign.
 * Requires authentication and workspace access.
 */
export const getCampaignAttachmentsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<CampaignAttachmentResponse[]> => {
    logInfo("campaigns.get_attachments.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await getCampaignAttachmentsApiWorkspacesWorkspaceIdCampaignsCampaignIdAttachmentsGet(
          data.workspaceId,
          data.campaignId,
        );

      if (response.status !== 200) {
        logError("campaigns.get_attachments.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          response_data: response.data,
        });
        // 返回空数组而不是抛出异常，避免影响整个页面加载
        return [];
      }

      // Filter duplicate attachments
      // Type assertion: status 200 guarantees response.data is the success type
      const filteredAttachments = filterDuplicateAttachments(
        response.data as CampaignAttachmentResponse[],
      );

      // Calculate stats safely
      const totalAttachments = Array.isArray(response.data) ? response.data.length : 0;

      logInfo("campaigns.get_attachments.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total_attachments: totalAttachments,
      });

      return filteredAttachments;
    } catch (error) {
      logError("campaigns.get_attachments.error", {
        error: error instanceof Error ? error.message : String(error),
        error_stack: error instanceof Error ? error.stack : undefined,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      // 返回空数组而不是抛出异常，避免影响整个页面加载
      return [];
    }
  });

/**
 * Filter duplicate attachments by unique identifiers.
 *
 * For PDF attachments: uses object_key as the unique identifier
 * For WEBPAGE attachments: uses site_url as the unique identifier
 *
 * Keeps the most recent attachment for each unique identifier.
 *
 * @param attachments Array of campaign attachments
 * @returns Array with duplicates removed, keeping the most recent
 */
function filterDuplicateAttachments(
  attachments: CampaignAttachmentResponse[],
): CampaignAttachmentResponse[] {
  if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
    return attachments;
  }

  const seenIdentifiers = new Set<string>();
  const filtered: typeof attachments = [];

  // Sort by created_at descending to ensure we keep the most recent
  // Create a mutable copy to sort (API types are readonly)
  const sortedAttachments = [...attachments].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA; // Most recent first
  });

  for (const attachment of sortedAttachments) {
    let identifier: string;

    if (attachment.attachment_type === AttachmentType.PDF) {
      // For PDF attachments, use object_key as identifier
      if (!attachment.object_key) {
        continue; // Skip invalid PDF attachments without object_key
      }
      identifier = `pdf:${attachment.object_key}`;
    } else if (attachment.attachment_type === AttachmentType.WEBPAGE) {
      // For WEBPAGE attachments, use site_url as identifier
      if (!attachment.site_url) {
        continue; // Skip invalid WEBPAGE attachments without site_url
      }
      identifier = `webpage:${attachment.site_url}`;
    } else {
      // Unknown attachment type, skip
      continue;
    }

    // Only add if we haven't seen this identifier before
    if (!seenIdentifiers.has(identifier)) {
      seenIdentifiers.add(identifier);
      filtered.push(attachment);
    }
  }

  return filtered;
}

/**
 * Commit campaign business
 *
 * Server function that commits form data to database.
 * Called when user clicks "Next" button.
 */
export const commitCampaignBusinessServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; campaignId: string; formData: BusinessFormValues }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.business.commit.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const formData = data.formData;
      const businessType =
        Array.isArray(formData.business_type) && formData.business_type.length > 0
          ? formData.business_type.join(", ")
          : "";
      const payload = {
        ...formData,
        business_type: businessType,
      };
      const response = await customInstance<{
        data: { status: string };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/business/commit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status !== 200) {
        logError("campaigns.business.commit.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to commit business info",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.business.commit.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        products_count: sanitizeStringArray(formData.products)?.length ?? 0,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.business.commit.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export interface CommitCampaignSupplierItemInput {
  supplier_key: string;
  name: string;
  domain?: string;
  website?: string;
  source?: string;
  logo?: string;
  location?: string;
  industry?: string;
  employee_range?: string;
  founded_year?: string;
  company_type?: string;
  description?: string;
  products?: string[];
  main_product_images?: Array<{
    title?: string;
    imageUrl: string;
    productUrl?: string;
  }>;
  compliance?: string[];
  categories?: string[];
  links?: string[];
  gold_years?: string;
  online_revenue?: string;
  factory_rank?: string;
  services?: string[];
  criteria_matches?: Array<{
    criteria: string;
    is_match: boolean;
    match_reason: string;
    required: boolean;
    references?: Array<{
      title?: string | null;
      url: string;
      snippet?: string | null;
      source_type?: "official_site" | "social" | "marketplace" | "search" | "other";
      favicon_url?: string | null;
    }>;
  }>;
}

function sanitizeOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function sanitizeRequiredString(value: unknown): string {
  return sanitizeOptionalString(value) ?? "";
}

function sanitizeStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value
    .map((item) => sanitizeOptionalString(item))
    .filter((item): item is string => Boolean(item));

  return normalized.length > 0 ? Array.from(new Set(normalized)) : undefined;
}

function sanitizeRecordArray(value: unknown): Array<Record<string, unknown>> | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value.filter((item): item is Record<string, unknown> =>
    Boolean(item && typeof item === "object" && !Array.isArray(item)),
  );

  return normalized.length > 0 ? normalized : undefined;
}

function sanitizeCommitCampaignSupplierItem(
  supplier: CommitCampaignSupplierItemInput | CampaignSupplierListItem,
): CommitCampaignSupplierItemInput {
  return {
    supplier_key: sanitizeRequiredString(supplier.supplier_key),
    name: sanitizeRequiredString(supplier.name),
    domain: sanitizeOptionalString(supplier.domain),
    website: sanitizeOptionalString(supplier.website),
    source: sanitizeOptionalString(supplier.source),
    logo: sanitizeOptionalString(supplier.logo),
    location: sanitizeOptionalString(supplier.location),
    industry: sanitizeOptionalString(supplier.industry),
    employee_range: sanitizeOptionalString(supplier.employee_range),
    founded_year: sanitizeOptionalString(supplier.founded_year),
    company_type: sanitizeOptionalString(supplier.company_type),
    description: sanitizeOptionalString(supplier.description),
    products: sanitizeStringArray(supplier.products),
    main_product_images: sanitizeRecordArray(supplier.main_product_images) as
      | CommitCampaignSupplierItemInput["main_product_images"]
      | undefined,
    compliance: sanitizeStringArray(supplier.compliance),
    categories: sanitizeStringArray(supplier.categories),
    links: sanitizeStringArray(supplier.links),
    gold_years: sanitizeOptionalString(supplier.gold_years),
    online_revenue: sanitizeOptionalString(supplier.online_revenue),
    factory_rank: sanitizeOptionalString(supplier.factory_rank),
    services: sanitizeStringArray(supplier.services),
    criteria_matches: sanitizeRecordArray(supplier.criteria_matches) as
      | CommitCampaignSupplierItemInput["criteria_matches"]
      | undefined,
  };
}

export interface CampaignSupplierListItem {
  id: string;
  campaign_id: string;
  company_id?: string | null;
  supplier_key: string;
  name: string;
  domain?: string | null;
  website?: string | null;
  source?: string | null;
  logo?: string | null;
  location?: string | null;
  industry?: string | null;
  employee_range?: string | null;
  founded_year?: string | null;
  company_type?: string | null;
  description?: string | null;
  products: string[];
  main_product_images: Array<{
    title?: string;
    imageUrl: string;
    productUrl?: string;
  }>;
  compliance: string[];
  categories: string[];
  links: string[];
  gold_years?: string | null;
  online_revenue?: string | null;
  factory_rank?: string | null;
  services: string[];
  criteria_matches: Array<{
    criteria: string;
    is_match: boolean;
    match_reason: string;
    required: boolean;
    references?: Array<{
      title?: string | null;
      url: string;
      snippet?: string | null;
      source_type?: "official_site" | "social" | "marketplace" | "search" | "other";
      favicon_url?: string | null;
    }>;
  }>;
  ranking?: number | null;
  search_text?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignSupplierListResponse {
  total: number;
  items: CampaignSupplierListItem[];
}

export interface CommitCampaignRfqFormInput {
  product?: string;
  requirements: string;
  order_quantity?: string;
  country?: string;
  delivery_time?: string;
}

export interface CreateAutomationTaskInput {
  workspaceId: string;
  sourceCampaignId: string;
  campaignName: string;
  chatSessionId?: string | null;
  rfq: CommitCampaignRfqFormInput;
  setting: {
    sender_name: string;
    sender_job_title: string;
    sender_accounts: SenderAccountWithChannel[];
    campaign_name?: string;
    knowledge_attachment_ids: string[];
    language: string;
    timezone: string;
  };
  suppliers: Array<CommitCampaignSupplierItemInput | CampaignSupplierListItem>;
  supplierSearchText?: string | null;
}

export interface CreateSalesAutomationTaskInput {
  workspaceId: string;
  sourceCampaignId: string;
  campaignName: string;
  chatSessionId?: string | null;
  targetProfile: {
    searchText: string;
    criteria: Array<{ text: string; required?: boolean }>;
  };
  setting: Omit<SettingFormValues, "sender_account_ids"> & {
    sender_accounts: SenderAccountWithChannel[];
  };
  suppliers?: Array<CommitCampaignSupplierItemInput | CampaignSupplierListItem>;
}

interface SavedCampaignICPResponse {
  business_model?: string | null;
  campaign_id: string;
  countries?: string[] | null;
  created_at: string;
  criteria: Record<string, object>[] | null;
  filters: Record<string, object>;
  find_leads_last_run_at: string | null;
  id: string;
  industry?: string | null;
  last_run_at: string | null;
  realtime_provider_last_run_at: string | null;
  search_query: string;
  sequence: number;
  source: string | null;
  status: string;
  updated_at: string;
}

function buildInquiryProductFromIcp(
  icp: { search_query?: string | null; criteria?: Array<{ text?: string | null }> | null } | null,
): SerializableInquiryProduct | null {
  const searchQuery = icp?.search_query?.trim();
  if (!searchQuery) {
    return null;
  }

  return {
    search_query: searchQuery,
    criteria: (icp?.criteria ?? [])
      .map((criterion) => criterion.text?.trim() ?? "")
      .filter((text) => text.length > 0),
  };
}

export const commitCampaignSuppliersServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      searchText?: string;
      suppliers: CommitCampaignSupplierItemInput[];
    }) => ({
      workspaceId: data.workspaceId,
      campaignId: data.campaignId,
      searchText: sanitizeOptionalString(data.searchText),
      suppliers: Array.isArray(data.suppliers)
        ? data.suppliers.map((supplier) => sanitizeCommitCampaignSupplierItem(supplier))
        : [],
    }),
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.suppliers.commit.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      supplier_count: data.suppliers.length,
    });

    try {
      const response = await customInstance<{
        data: { status: string; total: number };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/suppliers/commit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          search_text: data.searchText ?? "",
          suppliers: data.suppliers,
        }),
      });

      if (response.status !== 200) {
        logError("campaigns.suppliers.commit.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to commit campaign suppliers",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.suppliers.commit.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total: response.data.total,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.suppliers.commit.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const commitCampaignRfqFormServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; campaignId: string; payload: CommitCampaignRfqFormInput }) =>
      data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.rfq_form.commit.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });
    try {
      const response = await customInstance<{
        data: { status: string };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/rfq/commit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.payload),
      });

      if (response.status !== 200) {
        logError("campaigns.rfq_form.commit.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to commit campaign rfq form",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.rfq_form.commit.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      return response.data;
    } catch (error) {
      logError("campaigns.rfq_form.commit.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const createAutomationTaskServer = createServerFn({ method: "POST" })
  .validator(
    (data: CreateAutomationTaskInput): CreateAutomationTaskInput => ({
      workspaceId: data.workspaceId,
      sourceCampaignId: data.sourceCampaignId,
      campaignName:
        sanitizeOptionalString(data.campaignName) ??
        sanitizeOptionalString(data.rfq.product) ??
        "Automation Task",
      chatSessionId: sanitizeOptionalString(data.chatSessionId),
      rfq: {
        product: sanitizeOptionalString(data.rfq.product),
        requirements: sanitizeRequiredString(data.rfq.requirements),
        order_quantity: sanitizeOptionalString(data.rfq.order_quantity),
        country: sanitizeOptionalString(data.rfq.country),
        delivery_time: sanitizeOptionalString(data.rfq.delivery_time),
      },
      setting: {
        sender_name: sanitizeRequiredString(data.setting.sender_name),
        sender_job_title: sanitizeRequiredString(data.setting.sender_job_title),
        sender_accounts: data.setting.sender_accounts,
        campaign_name: sanitizeOptionalString(data.setting.campaign_name),
        knowledge_attachment_ids: Array.isArray(data.setting.knowledge_attachment_ids)
          ? data.setting.knowledge_attachment_ids
          : [],
        language: sanitizeRequiredString(data.setting.language),
        timezone: sanitizeRequiredString(data.setting.timezone),
      },
      suppliers: Array.isArray(data.suppliers)
        ? data.suppliers.map((supplier) => sanitizeCommitCampaignSupplierItem(supplier))
        : [],
      supplierSearchText: sanitizeOptionalString(data.supplierSearchText),
    }),
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.automation_task.create.server_fn_called", {
      workspace_id: data.workspaceId,
      source_campaign_id: data.sourceCampaignId,
      supplier_count: data.suppliers.length,
    });

    try {
      const response = await customInstance<{
        data: { campaign: GeneratedCampaignResponse; supplier_total: number };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/automation-tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          campaign_name: data.campaignName,
          chat_session_id: data.chatSessionId,
          rfq: data.rfq,
          setting: data.setting,
          suppliers: data.suppliers,
          supplier_search_text: data.supplierSearchText,
        }),
      });

      if (response.status !== 201) {
        const errorMessage = extractApiErrorMessage(response, "Failed to create automation task");
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      const launchedCampaign = normalizeCampaignResponse(response.data.campaign);

      logInfo("campaigns.automation_task.create.success", {
        workspace_id: data.workspaceId,
        source_campaign_id: data.sourceCampaignId,
        task_campaign_id: launchedCampaign.id,
        supplier_count: response.data.supplier_total,
      });

      return {
        campaign: launchedCampaign,
        supplierTotal: response.data.supplier_total,
      };
    } catch (error) {
      logError("campaigns.automation_task.create.error", {
        workspace_id: data.workspaceId,
        source_campaign_id: data.sourceCampaignId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const createSalesAutomationTaskServer = createServerFn({ method: "POST" })
  .validator(
    (data: CreateSalesAutomationTaskInput): CreateSalesAutomationTaskInput => ({
      workspaceId: data.workspaceId,
      sourceCampaignId: data.sourceCampaignId,
      campaignName: sanitizeOptionalString(data.campaignName) ?? "Automation Task",
      chatSessionId: sanitizeOptionalString(data.chatSessionId),
      targetProfile: {
        searchText: sanitizeRequiredString(data.targetProfile.searchText),
        criteria: Array.isArray(data.targetProfile.criteria)
          ? data.targetProfile.criteria
              .map((criterion) => ({
                text: sanitizeOptionalString(criterion.text),
                required: criterion.required ?? true,
              }))
              .filter((criterion): criterion is { text: string; required: boolean } =>
                Boolean(criterion.text),
              )
          : [],
      },
      setting: {
        ...data.setting,
        sender_name: sanitizeRequiredString(data.setting.sender_name),
        sender_job_title: sanitizeRequiredString(data.setting.sender_job_title),
        sender_accounts: data.setting.sender_accounts,
        campaign_name: sanitizeOptionalString(data.setting.campaign_name),
        knowledge_attachment_ids: Array.isArray(data.setting.knowledge_attachment_ids)
          ? data.setting.knowledge_attachment_ids
          : [],
        language: data.setting.language,
        timezone: data.setting.timezone,
        sequence: Array.isArray(data.setting.sequence) ? data.setting.sequence : [],
      },
      suppliers: Array.isArray(data.suppliers)
        ? data.suppliers.map((supplier) => sanitizeCommitCampaignSupplierItem(supplier))
        : [],
    }),
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.sales_automation_task.create.server_fn_called", {
      workspace_id: data.workspaceId,
      source_campaign_id: data.sourceCampaignId,
      sequence_steps: data.setting.sequence.length,
      criteria_count: data.targetProfile.criteria.length,
      supplier_count: data.suppliers?.length ?? 0,
    });

    try {
      const response = await customInstance<{
        data: { campaign: GeneratedCampaignResponse; supplier_total?: number };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/sales-automation-tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          campaign_name: data.campaignName,
          chat_session_id: data.chatSessionId,
          target_profile: {
            search_text: data.targetProfile.searchText,
            criteria: data.targetProfile.criteria.map((criterion) => ({
              text: criterion.text,
              required: criterion.required ?? true,
            })),
          },
          setting: {
            sender_name: data.setting.sender_name,
            sender_job_title: data.setting.sender_job_title,
            sender_accounts: data.setting.sender_accounts,
            campaign_name: data.setting.campaign_name,
            sequence: data.setting.sequence.map((step) => ({
              delay: {
                amount: step.delay.amount,
                unit: step.delay.unit,
              },
              channel: step.channel,
              subject: step.subject,
              message_type: step.message_type,
              template: step.template,
            })),
            knowledge_attachment_ids: data.setting.knowledge_attachment_ids,
            language: data.setting.language,
            timezone: data.setting.timezone,
            schedule: {
              days: {
                monday: data.setting.schedule.days.monday,
                tuesday: data.setting.schedule.days.tuesday,
                wednesday: data.setting.schedule.days.wednesday,
                thursday: data.setting.schedule.days.thursday,
                friday: data.setting.schedule.days.friday,
                saturday: data.setting.schedule.days.saturday,
                sunday: data.setting.schedule.days.sunday,
              },
            },
          },
          suppliers: data.suppliers ?? [],
        }),
      });

      if (response.status !== 201) {
        const errorMessage = extractApiErrorMessage(
          response,
          "Failed to create sales automation task",
        );
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      const launchedCampaign = normalizeCampaignResponse(response.data.campaign);

      logInfo("campaigns.sales_automation_task.create.success", {
        workspace_id: data.workspaceId,
        source_campaign_id: data.sourceCampaignId,
        task_campaign_id: launchedCampaign.id,
        supplier_count: response.data.supplier_total ?? 0,
      });

      return {
        campaign: launchedCampaign,
        supplierTotal: response.data.supplier_total ?? 0,
      };
    } catch (error) {
      logError("campaigns.sales_automation_task.create.error", {
        workspace_id: data.workspaceId,
        source_campaign_id: data.sourceCampaignId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const getCampaignRfqFormServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.rfq_form.get.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response = await getCampaignRfqFormApiWorkspacesWorkspaceIdCampaignsCampaignIdRfqGet(
        data.workspaceId,
        data.campaignId,
      );

      if (response.status !== 200) {
        logError("campaigns.rfq_form.get.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get campaign rfq form",
            status: response.status,
          }),
        );
      }

      return response.data;
    } catch (error) {
      logError("campaigns.rfq_form.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const listCampaignSuppliersServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.suppliers.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response = await customInstance<{
        data: CampaignSupplierListResponse;
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/suppliers`, {
        method: "GET",
      });

      if (response.status !== 200) {
        logError("campaigns.suppliers.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaign suppliers",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.suppliers.list.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total: response.data.total,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.suppliers.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const listCampaignQuotesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.quotes.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response = await listCampaignQuotesApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesGet(
        data.workspaceId,
        data.campaignId,
      );

      if (response.status !== 200) {
        logError("campaigns.quotes.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list campaign quotes",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.quotes.list.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total: response.data.total,
      });

      let inquiryProduct: SerializableInquiryProduct | null = null;
      try {
        const icpResponse =
          await listCampaignIcpsApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpsGet(
            data.workspaceId,
            data.campaignId,
            { status: "confirmed" },
          );

        if (icpResponse.status === 200) {
          const icpList = icpResponse.data as CampaignICPListResponse;
          inquiryProduct = buildInquiryProductFromIcp(
            (icpList.icps[0] ?? null) as SavedCampaignICPResponse | null,
          );
        } else {
          logError("campaigns.quotes.inquiry_product.failed", {
            status: icpResponse.status,
            workspace_id: data.workspaceId,
            campaign_id: data.campaignId,
          });
        }
      } catch (error) {
        logError("campaigns.quotes.inquiry_product.error", {
          error: error instanceof Error ? error.message : "unknown-error",
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
      }

      return normalizeCampaignQuoteListResponse(response.data, inquiryProduct);
    } catch (error) {
      logError("campaigns.quotes.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const updateCampaignQuoteStatusServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      quoteId: string;
      status: CampaignQuoteStatus;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.quotes.update.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      quote_id: data.quoteId,
      status: data.status,
    });

    try {
      const payload: CampaignQuoteStatusUpdateRequest = {
        status: data.status,
      };
      const response =
        await updateCampaignQuoteStatusApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesQuoteIdPatch(
          data.workspaceId,
          data.campaignId,
          data.quoteId,
          payload,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to update campaign quote status";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.quotes.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          quote_id: data.quoteId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.quotes.update.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        quote_id: data.quoteId,
        status: response.data.status,
      });

      return normalizeCampaignQuoteResponse(response.data);
    } catch (error) {
      const fallbackMessage = "Failed to update campaign quote status";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.quotes.update.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        quote_id: data.quoteId,
        status,
      });
      throw error;
    }
  });

export const replyCampaignQuoteSupplierQuestionServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      quoteId: string;
      questionId: string;
      answer: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.quotes.supplier_question_reply.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      quote_id: data.quoteId,
      question_id: data.questionId,
    });

    try {
      const payload: CampaignQuoteSupplierQuestionReplyRequest = {
        question_id: data.questionId,
        answer: data.answer,
      };
      const response =
        await replyCampaignQuoteSupplierQuestionApiWorkspacesWorkspaceIdCampaignsCampaignIdQuotesQuoteIdSupplierQuestionReplyPost(
          data.workspaceId,
          data.campaignId,
          data.quoteId,
          payload,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to reply to supplier question";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.quotes.supplier_question_reply.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          quote_id: data.quoteId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.quotes.supplier_question_reply.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        quote_id: data.quoteId,
        draft_id: response.data.draft_id,
        send_task_id: response.data.send_task_id,
      });

      return {
        ...response.data,
        quote: normalizeCampaignQuoteResponse(response.data.quote),
      };
    } catch (error) {
      const fallbackMessage = "Failed to reply to supplier question";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.quotes.supplier_question_reply.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        quote_id: data.quoteId,
        status,
      });
      throw error;
    }
  });

export const addProductToCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; productId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.products.add.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      product_id: data.productId,
    });

    try {
      const response =
        await addProductToCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsProductIdPost(
          data.workspaceId,
          data.campaignId,
          data.productId,
        );

      if (response.status !== 201) {
        const fallbackMessage = "Failed to add product to campaign";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.products.add.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          product_id: data.productId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.products.add.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        product_id: data.productId,
      });

      return response.data;
    } catch (error) {
      const fallbackMessage = "Failed to add product to campaign";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.products.add.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        product_id: data.productId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const removeProductFromCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; productId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.products.remove.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      product_id: data.productId,
    });

    try {
      const response =
        await removeProductFromCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsProductIdDelete(
          data.workspaceId,
          data.campaignId,
          data.productId,
        );

      if (response.status !== 204) {
        const fallbackMessage = "Failed to remove product from campaign";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.products.remove.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          product_id: data.productId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.products.remove.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        product_id: data.productId,
      });
    } catch (error) {
      const fallbackMessage = "Failed to remove product from campaign";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.products.remove.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        product_id: data.productId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const listCampaignProductsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.products.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await listCampaignProductsApiWorkspacesWorkspaceIdCampaignsCampaignIdProductsGet(
          data.workspaceId,
          data.campaignId,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to list campaign products";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.products.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.products.list.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total: response.data.total,
      });

      return response.data;
    } catch (error) {
      const fallbackMessage = "Failed to list campaign products";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.products.list.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const listCampaignKnowledgesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<CampaignKnowledgeLinkListResponse> => {
    logInfo("campaigns.knowledges.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await listCampaignKnowledgesApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesGet(
          data.workspaceId,
          data.campaignId,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to list campaign knowledges";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.knowledges.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.knowledges.list.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        total: response.data.total,
      });

      return response.data;
    } catch (error) {
      const fallbackMessage = "Failed to list campaign knowledges";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.knowledges.list.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const addKnowledgeToCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; knowledgeSourceId: string }) => data)
  .handler(async ({ data }): Promise<CampaignKnowledgeLinkResponse> => {
    logInfo("campaigns.knowledges.add.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      knowledge_source_id: data.knowledgeSourceId,
    });

    try {
      const response =
        await addKnowledgeToCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesKnowledgeSourceIdPost(
          data.workspaceId,
          data.campaignId,
          data.knowledgeSourceId,
        );

      if (response.status !== 201) {
        const fallbackMessage = "Failed to add knowledge to campaign";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.knowledges.add.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          knowledge_source_id: data.knowledgeSourceId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.knowledges.add.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        knowledge_source_id: data.knowledgeSourceId,
      });

      return response.data;
    } catch (error) {
      const fallbackMessage = "Failed to add knowledge to campaign";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.knowledges.add.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        knowledge_source_id: data.knowledgeSourceId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const removeKnowledgeFromCampaignServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; knowledgeSourceId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.knowledges.remove.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      knowledge_source_id: data.knowledgeSourceId,
    });

    try {
      const response =
        await removeKnowledgeFromCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdKnowledgesKnowledgeSourceIdDelete(
          data.workspaceId,
          data.campaignId,
          data.knowledgeSourceId,
        );

      if (response.status !== 204) {
        const fallbackMessage = "Failed to remove knowledge from campaign";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.knowledges.remove.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          knowledge_source_id: data.knowledgeSourceId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.knowledges.remove.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        knowledge_source_id: data.knowledgeSourceId,
      });
    } catch (error) {
      const fallbackMessage = "Failed to remove knowledge from campaign";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.knowledges.remove.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        knowledge_source_id: data.knowledgeSourceId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const listCustomerCasesServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<CustomerCaseListResponse> => {
    logInfo("campaigns.customer_cases.list.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await listCustomerCasesApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesGet(
          data.workspaceId,
          data.campaignId,
        );

      if (response.status !== 200) {
        logError("campaigns.customer_cases.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to list customer cases",
            status: response.status,
          }),
        );
      }

      return response.data;
    } catch (error) {
      logError("campaigns.customer_cases.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const createCustomerCaseServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; campaignId: string; request: CustomerCaseCreateRequest }) => data,
  )
  .handler(async ({ data }): Promise<CustomerCaseResponse> => {
    logInfo("campaigns.customer_cases.create.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      name: data.request.name,
    });

    try {
      const response =
        await createCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesPost(
          data.workspaceId,
          data.campaignId,
          data.request,
        );

      if (response.status !== 201) {
        const fallbackMessage = "Failed to create customer case";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.customer_cases.create.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      return response.data as CustomerCaseResponse;
    } catch (error) {
      const fallbackMessage = "Failed to create customer case";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.customer_cases.create.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const updateCustomerCaseServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      caseId: string;
      request: CustomerCaseUpdateRequest;
    }) => data,
  )
  .handler(async ({ data }): Promise<CustomerCaseResponse> => {
    logInfo("campaigns.customer_cases.update.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      case_id: data.caseId,
    });

    try {
      const response =
        await updateCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesCaseIdPut(
          data.workspaceId,
          data.campaignId,
          data.caseId,
          data.request,
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to update customer case";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.customer_cases.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          case_id: data.caseId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      return response.data as CustomerCaseResponse;
    } catch (error) {
      const fallbackMessage = "Failed to update customer case";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.customer_cases.update.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        case_id: data.caseId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

export const deleteCustomerCaseServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; caseId: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    logInfo("campaigns.customer_cases.delete.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      case_id: data.caseId,
    });

    try {
      const response =
        await deleteCustomerCaseApiWorkspacesWorkspaceIdCampaignsCampaignIdCustomerCasesCaseIdDelete(
          data.workspaceId,
          data.campaignId,
          data.caseId,
        );

      if (response.status !== 204) {
        const fallbackMessage = "Failed to delete customer case";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);

        logError("campaigns.customer_cases.delete.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          case_id: data.caseId,
          error: errorMessage,
        });

        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.customer_cases.delete.completed", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        case_id: data.caseId,
      });
    } catch (error) {
      const fallbackMessage = "Failed to delete customer case";
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      const status = extractApiErrorStatus(error) ?? 0;

      logError("campaigns.customer_cases.delete.error", {
        error: errorMessage,
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        case_id: data.caseId,
        status,
      });

      throw new Error(
        JSON.stringify({
          detail: errorMessage,
          status,
        }),
      );
    }
  });

/**
 * 持久化 ICP 搜索语句
 */
export const commitCampaignICPServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      formData: ICPFormValues;
      source: string;
      reasoning?: string | null;
      autoGenerated?: boolean;
      apolloFilters?: Record<string, unknown> | null;
      sequence?: number;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.icp.commit.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      source: data.source,
      auto_generated: data.autoGenerated,
      has_apollo_filters: !!data.apolloFilters,
    });

    try {
      const criteria = getMeaningfulCriteria(data.formData.criteria);
      const response = await customInstance<{
        data: {
          id: string;
          search_query: string;
          source: string | null;
        };
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/icp/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          search_query: data.formData.search.trim(),
          source: data.source,
          reasoning: data.reasoning,
          auto_generated: data.autoGenerated,
          apollo_filters: data.apolloFilters,
          criteria,
          sequence: data.sequence,
        }),
      });

      if (response.status !== 200) {
        const fallbackMessage = "保存 ICP 搜索语句失败，请稍后重试。";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);
        logError("campaigns.icp.commit.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          error: errorMessage,
        });
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.icp.commit.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        source: response.data.source,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.icp.commit.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const getSavedCampaignICPServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }): Promise<SavedCampaignICPResponse | null> => {
    logInfo("campaigns.icp.saved.get.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response = await listCampaignIcpsApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpsGet(
        data.workspaceId,
        data.campaignId,
        { status: "confirmed" },
      );

      if (response.status !== 200) {
        logError("campaigns.icp.saved.get.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to load saved campaign ICP",
            status: response.status,
          }),
        );
      }

      const icpList = response.data as CampaignICPListResponse;
      const savedIcp = icpList.icps[0] ?? null;

      logInfo("campaigns.icp.saved.get.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        found: Boolean(savedIcp),
      });

      if (!savedIcp) {
        return null;
      }

      return toPlainJson(savedIcp) as SavedCampaignICPResponse;
    } catch (error) {
      logError("campaigns.icp.saved.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const validateICPCoverageServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; searchQuery: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.icp.validate_coverage.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      search_query_length: data.searchQuery.length,
    });

    try {
      const response =
        await validateIcpCoverageApiWorkspacesWorkspaceIdCampaignsCampaignIdIcpValidateCoveragePost(
          data.workspaceId,
          data.campaignId,
          { search_query: data.searchQuery },
          { timeoutMs: 60000 },
        );

      if (response.status !== 200) {
        logError("campaigns.icp.validate_coverage.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to validate ICP coverage",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.icp.validate_coverage.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        coverage_rate: response.data.coverage_rate,
        missing_fields_count: response.data.missing_fields?.length ?? 0,
      });

      const requiredCoverage = {
        location: Boolean(response.data.coverage?.location),
        industry: Boolean(response.data.coverage?.industry),
      };
      const requiredMissingFields = [
        ...(!requiredCoverage.location ? ["Location"] : []),
        ...(!requiredCoverage.industry ? ["Industry"] : []),
      ];
      const normalizedCoverageRate =
        requiredMissingFields.length === 0 ? 1 : 1 - requiredMissingFields.length / 2;

      return {
        ...response.data,
        missing_fields: requiredMissingFields,
        coverage_rate: normalizedCoverageRate,
        suggestions:
          requiredMissingFields.length > 0
            ? `Suggested to add the following information: ${requiredMissingFields.join(", ")}`
            : null,
      };
    } catch (error) {
      logError("campaigns.icp.validate_coverage.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Get OSS upload signature
 *
 * Server function that retrieves OSS upload signature for direct file upload.
 * Supports PDF and logo file types.
 */
export const getOSSUploadSignatureServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      fileType: "pdf" | "logo";
      filename: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.oss_signature.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      file_type: data.fileType,
      filename: data.filename,
    });

    try {
      const response =
        await getOssUploadSignatureApiWorkspacesWorkspaceIdCampaignsCampaignIdOssUploadSignaturePost(
          data.workspaceId,
          data.campaignId,
          {
            file_type: data.fileType,
            filename: data.filename,
          },
        );

      if (response.status !== 200) {
        logError("campaigns.oss_signature.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get OSS upload signature",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.oss_signature.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        object_key: response.data.object_key,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.oss_signature.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Create PDF attachment record
 *
 * Server function that creates an attachment record after successful OSS upload.
 */
export const createPDFAttachmentServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      attachmentId: string;
      objectKey: string;
      fileSize: number;
      filename: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.attachment.create.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      attachment_id: data.attachmentId,
    });

    try {
      const response =
        await createAttachmentRecordApiWorkspacesWorkspaceIdCampaignsCampaignIdAttachmentsPost(
          data.workspaceId,
          data.campaignId,
          {
            attachment_id: data.attachmentId,
            attachment_type: AttachmentType.PDF,
            object_key: data.objectKey,
            file_size: data.fileSize,
            original_filename: data.filename,
          },
        );

      if (response.status !== 201) {
        logError("campaigns.attachment.create.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to create attachment record",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.attachment.create.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        attachment_id: response.data.id,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.attachment.create.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Update business logo
 *
 * Server function that updates the logo of a campaign business.
 */
export const updateBusinessLogoServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; logoOssKey: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.business.logo.update.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      logo_oss_key: data.logoOssKey,
    });

    try {
      const response =
        await updateCampaignBusinessLogoApiWorkspacesWorkspaceIdCampaignsCampaignIdBusinessLogoPatch(
          data.workspaceId,
          data.campaignId,
          {
            logo_oss_key: data.logoOssKey,
          },
        );

      if (response.status !== 200) {
        logError("campaigns.business.logo.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to update business logo",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.business.logo.update.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        logo_url: response.data.logo_url,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.business.logo.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Extract favicon logo from website and upload to OSS
 *
 * Server function that calls backend favicon extraction endpoint and returns logo OSS key.
 */
export const extractBusinessFaviconLogoServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; website: string }) => {
    const website = data.website.trim();
    if (!website) {
      throw new Error("Website is required");
    }
    return { ...data, website };
  })
  .handler(async ({ data }) => {
    logInfo("campaigns.business.favicon.extract.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      website: data.website,
    });

    try {
      const response = await customInstance<{ logo_oss_key: string; logo_url?: string | null }>(
        `/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/business/extract-favicon-logo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ website: data.website }),
        },
      );

      logInfo("campaigns.business.favicon.extract.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        logo_oss_key: response.logo_oss_key,
        has_logo_url: !!response.logo_url,
      });

      return response;
    } catch (error) {
      logError("campaigns.business.favicon.extract.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        website: data.website,
      });
      throw error;
    }
  });

/**
 * Update campaign title
 *
 * Server function that updates the campaign name (title).
 * Requires authentication and workspace access.
 */
export const updateCampaignTitleServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; campaignId: string; name: string }) => {
    // Validate name is not empty after trimming
    const trimmedName = data.name.trim();
    if (trimmedName.length === 0) {
      throw new Error("Campaign name cannot be empty");
    }
    return { ...data, name: trimmedName };
  })
  .handler(async ({ data }) => {
    logInfo("campaigns.update_title.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      new_name: data.name,
    });

    try {
      const response = await updateCampaignApiWorkspacesWorkspaceIdCampaignsCampaignIdPatch(
        data.workspaceId,
        data.campaignId,
        {
          name: data.name,
        },
      );

      if (response.status !== 200) {
        logError("campaigns.update_title.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to update campaign title",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.update_title.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        new_name: data.name,
      });

      return normalizeCampaignResponse(response.data);
    } catch (error) {
      logError("campaigns.update_title.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

/**
 * Save Campaign Setting
 *
 * Server function that saves campaign setting configuration including:
 * - Sender information (name, job title)
 * - Email accounts to use for sending
 * - Knowledge attachments
 * - Sequence steps
 * - Schedule configuration
 * - Language and timezone preferences
 */
export const getCampaignSettingServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; campaignId: string }) => data)
  .handler(async ({ data }) => {
    logInfo("campaigns.setting.get.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
    });

    try {
      const response =
        await getCampaignSettingApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingGet(
          data.workspaceId,
          data.campaignId,
        );

      // 404 is normal - setting doesn't exist yet
      // TypeScript doesn't know about 404/403 status codes from OpenAPI spec
      // so we need to check at runtime with type assertion
      const status = response.status as number;

      if (status === 404) {
        logInfo("campaigns.setting.get.not_found", {
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        return null;
      }

      if (status === 403) {
        logError("campaigns.setting.get.forbidden", {
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "You don't have permission to access this campaign setting",
            status: 403,
          }),
        );
      }

      if (response.status !== 200) {
        logError("campaigns.setting.get.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to load campaign setting",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.setting.get.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        setting_id: response.data.id,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.setting.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const saveCampaignSettingServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      formData: Omit<SettingFormValues, "sender_account_ids"> & {
        sender_accounts: SenderAccountWithChannel[];
      };
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.setting.save.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      sender_account_count: data.formData.sender_accounts?.length,
      sequence_steps: data.formData.sequence.length,
    });

    try {
      // Transform frontend SettingFormValues to backend CampaignSettingSaveRequest
      const requestBody = {
        sender_name: data.formData.sender_name,
        sender_job_title: data.formData.sender_job_title,
        sender_accounts: data.formData.sender_accounts,
        campaign_name: data.formData.campaign_name,
        sequence: data.formData.sequence.map((step) => ({
          delay: {
            amount: step.delay.amount,
            unit: step.delay.unit,
          },
          channel: step.channel,
          subject: step.subject,
          message_type: step.message_type,
          template: step.template,
        })),
        knowledge_attachment_ids: data.formData.knowledge_attachment_ids,
        language: data.formData.language,
        timezone: data.formData.timezone,
        schedule: {
          days: {
            monday: data.formData.schedule.days.monday,
            tuesday: data.formData.schedule.days.tuesday,
            wednesday: data.formData.schedule.days.wednesday,
            thursday: data.formData.schedule.days.thursday,
            friday: data.formData.schedule.days.friday,
            saturday: data.formData.schedule.days.saturday,
            sunday: data.formData.schedule.days.sunday,
          },
        },
      };

      const response =
        await saveCampaignSettingApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingPost(
          data.workspaceId,
          data.campaignId,
          requestBody,
        );

      if (response.status !== 201) {
        logError("campaigns.setting.save.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to save campaign setting",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.setting.save.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        setting_id: response.data.id,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.setting.save.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const updateCampaignSequenceServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; campaignId: string; sequence: SequenceStepRequest[] }) => data,
  )
  .handler(async ({ data }): Promise<CampaignSettingResponse> => {
    logInfo("campaigns.setting.sequence.update.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      sequence_steps: data.sequence.length,
    });

    try {
      const response =
        await updateCampaignSettingSequenceApiWorkspacesWorkspaceIdCampaignsCampaignIdSettingSequencePatch(
          data.workspaceId,
          data.campaignId,
          { sequence: data.sequence },
        );

      if (response.status !== 200) {
        const fallbackMessage = "Failed to update campaign sequence";
        const errorMessage = extractApiErrorMessage(response, fallbackMessage);
        logError("campaigns.setting.sequence.update.failed", {
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          status: response.status,
          error: errorMessage,
        });
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.setting.sequence.update.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
        setting_id: response.data.id,
      });

      return response.data;
    } catch (error) {
      logError("campaigns.setting.sequence.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });

export const saveCampaignSourcingSettingServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      formData: {
        sender_name: string;
        sender_job_title: string;
        sender_accounts: SenderAccountWithChannel[];
        campaign_name?: string;
        knowledge_attachment_ids: string[];
        language: string;
        timezone: string;
      };
    }) => data,
  )
  .handler(async ({ data }) => {
    logInfo("campaigns.setting.sourcing.save.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      sender_account_count: data.formData.sender_accounts?.length,
    });
    try {
      const response = await customInstance<{
        data: unknown;
        status: number;
        headers: Headers;
      }>(`/api/workspaces/${data.workspaceId}/campaigns/${data.campaignId}/setting/sourcing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.formData),
      });

      if (response.status !== 201) {
        const errorMessage = extractApiErrorMessage(
          response,
          "Failed to save sourcing campaign setting",
        );
        logError("campaigns.setting.sourcing.save.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
          error: errorMessage,
        });
        throw new Error(
          JSON.stringify({
            detail: errorMessage,
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.setting.sourcing.save.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      return response.data;
    } catch (error) {
      logError("campaigns.setting.sourcing.save.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });
